import { Europafacts } from "@/services/api";
import { useDatabaseStore } from "@/stores/database";
import { useUIStore } from "@/stores/ui";
import { logger } from "@/utils/debug";
import type { ActionHandler } from "@/types/stores/actions";
import type { DataflowState } from "@/types/stores/dataflow";
import type { Transformer } from "@/types/workspace";
import { useDataflowStore } from "./useDataflowStore";

/**
 * Helper function to check if a data input (data_input or user_file) exists in the dataflow
 * @param getDataflowState - Function to get the current dataflow state
 * @returns true if a data input exists, false otherwise
 */
function hasDataInput(getDataflowState: () => DataflowState): boolean {
  const state = getDataflowState();
  const activeDataflowId = state.activeDataflowId;
  if (!activeDataflowId) return false;

  const dataflow = state.dataflows[activeDataflowId];
  if (!dataflow?.flow_definition_json) return false;

  return dataflow.flow_definition_json.some(
    (transformer) =>
      transformer.type === "data_input" || transformer.type === "user_file"
  );
}

export const transformActions: Record<string, ActionHandler> = {
  /**
   * Selects the columns to be displayed in the dataflow
   * @param getDataflowState - The current state of the dataflow
   * @param _getUIState - The current state of the UI
   * @param payload - The payload containing the columns to be selected
   * @returns The transformer to be added to the dataflow
   */
  select: (getDataflowState, _getUIState, payload) => {
    if (!hasDataInput(getDataflowState)) return {};

    const { columns } = (payload as { columns?: string[] }) || {};

    const transformer: Transformer = {
      type: "select",
      item_list: columns || [],
    } as Transformer;

    const state = useDataflowStore.getState();
    if (!state.activeDataflowId) return {};

    const uiState = useUIStore.getState();
    const workspaceUI = uiState.getWorkspaceUI(state.activeDataflowId);
    const wasSidebarOpen =
      workspaceUI.sidebar.isOpen && workspaceUI.sidebar.mode === "flow";

    const activeId = state.activeDataflowId;
    if (wasSidebarOpen) {
      state.addTransformer(activeId, transformer);
    } else {
      setTimeout(() => {
        state.addTransformer(activeId, transformer);
      }, 300);
    }

    return {};
  },

  /**
   * Calculates the formula for the dataflow
   * @param getDataflowState - The current state of the dataflow
   * @param _getUIState - The current state of the UI
   * @param payload - The payload containing the formula to be calculated
   * @returns The transformer to be added to the dataflow
   */
  calculate: (getDataflowState, _getUIState, payload) => {
    if (!hasDataInput(getDataflowState)) return {};

    const { formula } = (payload as { formula?: string }) || {};

    const transformer: Transformer = {
      type: "calculate",
      item_list: [{ calc: formula || "", column: "calculated" }],
    } as Transformer;

    const { activeDataflowId } = getDataflowState();
    if (!activeDataflowId) return {};

    return {
      dataflows: {
        [activeDataflowId]: {
          flow_definition_json: [
            ...(getDataflowState().dataflows[activeDataflowId]
              ?.flow_definition_json || []),
            transformer,
          ],
        },
      },
    };
  },

  /**
   * Adds a where condition to the dataflow
   * @param getDataflowState - The current state of the dataflow
   * @param _getUIState - The current state of the UI
   * @param payload - The payload containing the condition to be added
   * @returns The transformer to be added to the dataflow
   */
  where: (getDataflowState, _getUIState, payload) => {
    if (!hasDataInput(getDataflowState)) return {};

    const { condition } = (payload as { condition?: string }) || {};

    const transformer: Transformer = {
      type: "where",
      item_list: [{ [condition || ""]: "value" }],
    } as Transformer;

    const { activeDataflowId } = getDataflowState();
    if (!activeDataflowId) return {};

    return {
      dataflows: {
        [activeDataflowId]: {
          flow_definition_json: [
            ...(getDataflowState().dataflows[activeDataflowId]
              ?.flow_definition_json || []),
            transformer,
          ],
        },
      },
    };
  },

  /**
   * Adds a where SQL condition to the dataflow
   * @param getDataflowState - The current state of the dataflow
   * @param _getUIState - The current state of the UI
   * @param payload - The payload containing the SQL condition to be added
   * @returns The transformer to be added to the dataflow
   */
  where_sql: (getDataflowState, _getUIState, payload) => {
    if (!hasDataInput(getDataflowState)) return {};

    const { sql } = (payload as { sql?: string }) || {};

    const transformer: Transformer = {
      type: "where_sql",
      where: sql || "",
    } as Transformer;

    const { activeDataflowId } = getDataflowState();
    if (!activeDataflowId) return {};

    return {
      dataflows: {
        [activeDataflowId]: {
          flow_definition_json: [
            ...(getDataflowState().dataflows[activeDataflowId]
              ?.flow_definition_json || []),
            transformer,
          ],
        },
      },
    };
  },

  /**
   * Sorts the dataflow by the columns
   * @param getDataflowState - The current state of the dataflow
   * @param _getUIState - The current state of the UI
   * @param payload - The payload containing the columns to be sorted
   * @returns The transformer to be added to the dataflow
   */
  sort: (getDataflowState, _getUIState, payload) => {
    if (!hasDataInput(getDataflowState)) return {};

    const { columns } =
      (payload as { columns?: string[]; direction?: string }) || {};

    const transformer: Transformer = {
      type: "sort",
      item_list: columns || [],
    } as Transformer;

    const { activeDataflowId } = getDataflowState();
    if (!activeDataflowId) return {};

    return {
      dataflows: {
        [activeDataflowId]: {
          flow_definition_json: [
            ...(getDataflowState().dataflows[activeDataflowId]
              ?.flow_definition_json || []),
            transformer,
          ],
        },
      },
    };
  },

  /**
   * Changes the type of the column
   * @param getDataflowState - The current state of the dataflow
   * @param _getUIState - The current state of the UI
   * @param payload - The payload containing the column and the new type
   * @returns The transformer to be added to the dataflow
   */
  column_type: (getDataflowState, _getUIState, payload) => {
    if (!hasDataInput(getDataflowState)) return {};

    const { column, newType } =
      (payload as { column?: string; newType?: string }) || {};

    const transformer: Transformer = {
      type: "column_type",
      item_list: [
        {
          column: column || "",
          new_type: newType || "",
        },
      ],
    } as Transformer;

    const { activeDataflowId } = getDataflowState();
    if (!activeDataflowId) return {};

    return {
      dataflows: {
        [activeDataflowId]: {
          flow_definition_json: [
            ...(getDataflowState().dataflows[activeDataflowId]
              ?.flow_definition_json || []),
            transformer,
          ],
        },
      },
    };
  },

  /**
   * Changes the name of the column
   * @param getDataflowState - The current state of the dataflow
   * @param _getUIState - The current state of the UI
   * @param payload - The payload containing the old name and the new name
   * @returns The transformer to be added to the dataflow
   */
  column_name: (getDataflowState, _getUIState, payload) => {
    if (!hasDataInput(getDataflowState)) return {};

    const { oldName, newName } =
      (payload as { oldName?: string; newName?: string }) || {};

    const transformer: Transformer = {
      type: "column_name",
      item_list: [
        {
          column: oldName || "",
          new_name: newName || "",
        },
      ],
    } as Transformer;

    const { activeDataflowId } = getDataflowState();
    if (!activeDataflowId) return {};

    return {
      dataflows: {
        [activeDataflowId]: {
          flow_definition_json: [
            ...(getDataflowState().dataflows[activeDataflowId]
              ?.flow_definition_json || []),
            transformer,
          ],
        },
      },
    };
  },

  /**
   * Fills the missing values in the dataflow
   * @param getDataflowState - The current state of the dataflow
   * @param _getUIState - The current state of the UI
   * @param _payload - The payload containing the column and the value to be filled
   * @returns The transformer to be added to the dataflow
   */
  fill_missing: (getDataflowState, _getUIState, _payload) => {
    if (!hasDataInput(getDataflowState)) return {};

    const transformer: Transformer = {
      type: "fill_missing",
      item_list: [
        {
          column: "column_name",
          value: "default_value",
        },
      ],
    } as Transformer;

    const { activeDataflowId } = getDataflowState();
    if (!activeDataflowId) return {};

    return {
      dataflows: {
        [activeDataflowId]: {
          flow_definition_json: [
            ...(getDataflowState().dataflows[activeDataflowId]
              ?.flow_definition_json || []),
            transformer,
          ],
        },
      },
    };
  },

  /**
   * Finds and replaces the values in the dataflow
   * @param getDataflowState - The current state of the dataflow
   * @param _getUIState - The current state of the UI
   * @param payload - The payload containing the find and replace values
   * @returns The transformer to be added to the dataflow
   */
  find_replace: (getDataflowState, _getUIState, payload) => {
    if (!hasDataInput(getDataflowState)) return {};

    const { find, replace } =
      (payload as { find?: string; replace?: string }) || {};

    const transformer: Transformer = {
      type: "find_replace",
      item_list: [
        {
          find: find || "",
          replace: replace || "",
          column: "column_name",
        },
      ],
    } as Transformer;

    const { activeDataflowId } = getDataflowState();
    if (!activeDataflowId) return {};

    return {
      dataflows: {
        [activeDataflowId]: {
          flow_definition_json: [
            ...(getDataflowState().dataflows[activeDataflowId]
              ?.flow_definition_json || []),
            transformer,
          ],
        },
      },
    };
  },

  /**
   * Changes the case of the column values
   * @param getDataflowState - The current state of the dataflow
   * @param _getUIState - The current state of the UI
   * @param payload - The payload containing the column and the case type
   * @returns The transformer to be added to the dataflow
   */
  upper_lower: (getDataflowState, _getUIState, payload) => {
    if (!hasDataInput(getDataflowState)) return {};

    const { column, case: caseType } =
      (payload as { column?: string; case?: string }) || {};

    const transformer: Transformer = {
      type: "upper_lower",
      item_list: [column || ""],
      operation: caseType || "lower",
    } as Transformer;

    const { activeDataflowId } = getDataflowState();
    if (!activeDataflowId) return {};

    return {
      dataflows: {
        [activeDataflowId]: {
          flow_definition_json: [
            ...(getDataflowState().dataflows[activeDataflowId]
              ?.flow_definition_json || []),
            transformer,
          ],
        },
      },
    };
  },

  /**
   * Joins the dataflow with another dataset
   * @param getDataflowState - The current state of the dataflow
   * @param _getUIState - The current state of the UI
   * @param payload - The payload containing the dataset and the join type
   * @returns The transformer to be added to the dataflow
   */
  join: (getDataflowState, _getUIState, payload) => {
    if (!hasDataInput(getDataflowState)) return {};

    const { dataset, type } =
      (payload as { dataset?: string; type?: string }) || {};

    const transformer: Transformer = {
      type: "join",
      source: "public",
      join_type: type || "left",
      db_table_id: dataset || "",
      new_column_list: [],
      join_column_list: [],
    } as Transformer;

    const { activeDataflowId } = getDataflowState();
    if (!activeDataflowId) return {};

    return {
      dataflows: {
        [activeDataflowId]: {
          flow_definition_json: [
            ...(getDataflowState().dataflows[activeDataflowId]
              ?.flow_definition_json || []),
            transformer,
          ],
        },
      },
    };
  },

  /**
   * Groups the dataflow by the columns
   * @param getDataflowState - The current state of the dataflow
   * @param _getUIState - The current state of the UI
   * @param payload - The payload containing the columns and the operation
   * @returns The transformer to be added to the dataflow
   */
  group: (getDataflowState, _getUIState, payload) => {
    if (!hasDataInput(getDataflowState)) return {};

    const { columns, operation } =
      (payload as { columns?: string[]; operation?: string }) || {};

    const transformer: Transformer = {
      type: "group",
      item_list: columns || [],
      total_list: [
        {
          column: "column_name",
          operation: operation || "sum",
        },
      ],
    } as Transformer;

    const { activeDataflowId } = getDataflowState();
    if (!activeDataflowId) return {};

    return {
      dataflows: {
        [activeDataflowId]: {
          flow_definition_json: [
            ...(getDataflowState().dataflows[activeDataflowId]
              ?.flow_definition_json || []),
            transformer,
          ],
        },
      },
    };
  },
};

// File action handlers
export const fileActions: Record<string, ActionHandler> = {
  /**
   * Creates a new dataflow
   * @param getDataflowState - The current state of the dataflow
   * @returns The modal to be opened
   */
  new: (getDataflowState) => {
    return {
      modal: {
        mode: "workspace",
        isOpen: true,
        title: "Create New Workspace",
        data: {
          defaultProjectId:
            getDataflowState().dataflows[
              getDataflowState().activeDataflowId || ""
            ]?.project_id,
        },
      },
    };
  },

  /**
   * Opens a file picker
   * @param _getDataflowState - The current state of the dataflow
   * @param _getUIState - The current state of the UI
   * @param _payload - The payload containing the file to be opened
   * @returns The modal to be opened
   */
  open: async (_getDataflowState, _getUIState, _payload) => {
    // Future: Could implement file picker or load from API
    return {};
  },

  /**
   * Saves the dataflow
   * @param getDataflowState - The current state of the dataflow
   * @returns The dataflow to be saved
   */
  save: async (getDataflowState) => {
    const { activeDataflowId, dataflows } = getDataflowState();
    const dataflowData = activeDataflowId
      ? dataflows[activeDataflowId]
      : undefined;

    if (!dataflowData) {
      logger.warn("No dataflow data to save");
      return {};
    }

    if (activeDataflowId) {
      useDataflowStore.getState().setSaving(activeDataflowId, true);
    }

    try {
      let savedDataflow;

      if (activeDataflowId) {
        savedDataflow = await Europafacts.updateDataflow(
          activeDataflowId,
          dataflowData
        );
      } else {
        const name = `Dataflow ${new Date().toISOString().split("T")[0]}`;
        savedDataflow = await Europafacts.saveDataflow(
          dataflowData,
          name,
          "User dataflow",
          dataflowData.project_id
        );
      }

      // Update dataflow store
      useDataflowStore.getState().updateDataflow(savedDataflow.dataflow_id, {
        ...dataflowData,
        dataflow_id: savedDataflow.dataflow_id,
      });

      // Update database store to sync projects array
      if (activeDataflowId) {
        const updateDatabaseDataflow =
          useDatabaseStore.getState().updateDataflow;
        await updateDatabaseDataflow(savedDataflow.dataflow_id, {
          dataflow_name: savedDataflow.dataflow_name,
          dataflow_description: savedDataflow.dataflow_description,
          flow_definition_json: savedDataflow.flow_definition_json,
          status: savedDataflow.status,
        });
      }

      if (activeDataflowId) {
        useDataflowStore.getState().setSaving(activeDataflowId, false);
      }

      return {};
    } catch (error) {
      logger.error("Failed to save dataflow:", error);

      if (activeDataflowId) {
        useDataflowStore.getState().setSaving(activeDataflowId, false);
      }

      return {};
    }
  },

  /**
   * Exports the dataflow
   * @param _getDataflowState - The current state of the dataflow
   * @param _getUIState - The current state of the UI
   * @param _payload - The payload containing the export options
   * @returns The modal to be opened
   */
  export: (_getDataflowState, _getUIState, _payload) => {
    return {};
  },
};

export const dataActions: Record<string, ActionHandler> = {
  /**
   * Toggles the datasets sidebar
   * @param _getDataflowState - The current state of the dataflow
   * @param getUIState - The current state of the UI
   * @param payload - Optional payload containing callback
   * @returns The sidebar state to be updated
   */
  datasets: (_getDataflowState, _getUIState) => {
    const uiStore = useUIStore.getState();
    const dataflowState = _getDataflowState();
    const dataflowId = dataflowState.activeDataflowId;
    if (!dataflowId) return {};
    
    const workspaceUI = uiStore.getWorkspaceUI(dataflowId);
    if (!workspaceUI || !workspaceUI.sidebar) {
      return {
        sidebar: { mode: "discover", isOpen: true },
      };
    }
    
    const shouldClose =
      workspaceUI.sidebar.isOpen && workspaceUI.sidebar.mode === "discover";
    
    return {
      sidebar: shouldClose
        ? { isOpen: false }
        : { mode: "discover", isOpen: true },
    };
  },

  /**
   * Uploads a file to the dataflow
   * @param _getDataflowState - The current state of the dataflow
   * @param _getUIState - The current state of the UI
   * @param _payload - The payload containing the file to be uploaded
   * @returns The modal to be opened
   */
  upload: async (_getDataflowState, _getUIState, _payload) => {
    return {};
  },

  /**
   * Opens the paste modal to paste data from clipboard
   * @param _getDataflowState - The current state of the dataflow
   * @param _getUIState - The current state of the UI
   * @returns The modal to be opened
   */
  paste: (_getDataflowState, _getUIState) => {
    return {
      modal: {
        mode: "paste",
        isOpen: true,
        title: "Paste Data from Clipboard",
      },
    };
  },
};

export const flowActions: Record<string, ActionHandler> = {
  /**
   * Toggles the flow sidebar
   * @param _getDataflowState - The current state of the dataflow
   * @param getUIState - The current state of the UI
   * @returns The sidebar state to be updated
   */
  run: async (_getDataflowState, _getUIState) => {
    const uiStore = useUIStore.getState();
    const dataflowState = _getDataflowState();
    const dataflowId = dataflowState.activeDataflowId;
    if (!dataflowId) return {};
    
    const workspaceUI = uiStore.getWorkspaceUI(dataflowId);
    if (!workspaceUI || !workspaceUI.sidebar) {
      return {
        sidebar: { mode: "flow", isOpen: true },
      };
    }
    
    const shouldClose =
      workspaceUI.sidebar.isOpen && workspaceUI.sidebar.mode === "flow";
    
    return {
      sidebar: shouldClose
        ? { isOpen: false }
        : { mode: "flow", isOpen: true },
    };
  },

  /**
   * Shares the dataflow
   * @param _getDataflowState - The current state of the dataflow
   * @param _getUIState - The current state of the UI
   * @param _payload - The payload containing the share options
   * @returns The modal to be opened
   */
  share: (_getDataflowState, _getUIState, _payload) => {
    // Future: Could open share modal
    return {};
  },
};
